import React, {Component} from "react"
import {Grid} from "react-bootstrap"
import {connect} from "react-redux"
import {match as Match} from "react-router-dom"

import {MarketingSubscriptionState, WellnessLiveNotification} from "@irati/types"
import {ReduxState} from "@irati/store/types"
import {InjectIntl, injectIntl, FormattedMessage} from "@irati/utils/lang"
import authentication from "@irati/auth"
import {hangPromise} from "@irati/utils/promise"
import History from "@irati/utils/history"
import Loading from "@irati/ui/src/Loading"
import marketingService from "@irati/marketing/services/marketing"
import onEnter from "@irati/utils/onEnter"
import {Toggle} from "@irati/ui/src/FormItem"
import GenericModal from "@irati/ui/src/GenericModal"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"
import {Body, H2, H3, H6} from "@irati/ui/src/Typography"
import successImage from "@irati/ui/images/success.svg"
import {isGlobalAdmin} from "@irati/identity/helpers/user/role"

import ConditionalWrapper from "@irati-web/admin/src/components/ConditionalWrapper"
import {CommunicationPreferences} from "./types"
import Banner from "./Banner"
import ContactPreference from "./ContactPreference"

import {getBasePreferences, buildPreferencesFromProfile, getPreferenceFromUrl, isCommunicationMethodEnabled} from "./helpers"

import {FlexBody, FlexH2, SubmitButton, LabelWrapper, ToggleWrapper, EmailLabel} from "./styled"

interface Props extends InjectIntl {
  match: Match<{userId: string; signature: string}>
  isMobile: boolean
  unAuthed?: boolean
}

interface State {
  load: Promise<any>
  isAdmin: boolean
  signature: string
  toggleAllEmail: boolean
  emailPreferences: CommunicationPreferences
  showSuccessModal?: boolean
  submit?: Promise<any>
  wellnessLiveNotifications: WellnessLiveNotification[]
}

const basePreferences = getBasePreferences()

class ContactPreferences extends Component<Props, State> {
  state: State = {
    load: hangPromise(),
    isAdmin: false,
    signature: "",
    toggleAllEmail: false,
    emailPreferences: {
      ...basePreferences
    },
    wellnessLiveNotifications: []
  }

  componentDidMount = () => {
    const {unAuthed} = this.props
    let load

    if (unAuthed) {
      const {
        match: {params}
      } = this.props

      load = marketingService.getProfileSettings(params.userId).then(({emailSubscribed, unsubscribedEmail}) => {
        const emailsEnabled =
          emailSubscribed === MarketingSubscriptionState.OptedIn || emailSubscribed === MarketingSubscriptionState.Subscribed

        this.setState((prevState: State) => {
          const resolvedEmailPreferences = buildPreferencesFromProfile(prevState.emailPreferences, emailsEnabled, unsubscribedEmail)

          return {
            ...prevState,
            signature: params.signature,
            toggleAllEmail: emailsEnabled,
            emailPreferences: {
              ...prevState.emailPreferences,
              ...resolvedEmailPreferences
            }
          }
        })
      })
    } else {
      load = marketingService.getProfile({userId: authentication.user().uuid}).then(({signature, settings}) => {
        const emailsEnabled =
          settings?.emailSubscribed === MarketingSubscriptionState.OptedIn ||
          settings?.emailSubscribed === MarketingSubscriptionState.Subscribed

        this.setState({
          wellnessLiveNotifications: settings?.wellnessLiveNotifications
        })

        this.setState((prevState: State) => {
          const isAdmin = isGlobalAdmin(authentication.business(), authentication.user())
          const resolvedEmailPreferences = buildPreferencesFromProfile(
            prevState.emailPreferences,
            emailsEnabled,
            settings?.unsubscribedEmail
          )

          return {
            ...prevState,
            isAdmin,
            signature,
            toggleAllEmail: emailsEnabled,
            emailPreferences: {
              ...prevState.emailPreferences,
              ...resolvedEmailPreferences
            }
          }
        })
      })
    }

    this.setState({load})
  }

  toggleSuccessModal = (show: boolean) => this.setState({showSuccessModal: show})

  updatePreference = (preference: keyof CommunicationPreferences, value: boolean) => {
    this.setState((prevState: State) => ({
      emailPreferences: {
        ...prevState.emailPreferences,
        [preference]: value
      }
    }))
  }

  updateAllPreferences = () => {
    this.setState((prevState: State) => ({
      ...prevState,
      toggleAllEmail: !prevState.toggleAllEmail,
      emailPreferences: {
        free_perks: !prevState.toggleAllEmail,
        weekly_newsletter: !prevState.toggleAllEmail,
        recognition_alerts: !prevState.toggleAllEmail,
        wellness_alerts: !prevState.toggleAllEmail,
        hr_tips: !prevState.toggleAllEmail,
        product_content: !prevState.toggleAllEmail,
        alcohol: !prevState.toggleAllEmail,
        fathers_day: !prevState.toggleAllEmail,
        mothers_day: !prevState.toggleAllEmail
      }
    }))
  }

  handleClickUnsubscribe = (preference: keyof CommunicationPreferences) => {
    this.setState((prevState: State) => ({
      emailPreferences: {
        ...prevState.emailPreferences,
        [preference]: false
      }
    }))
  }

  handleUpdate = () => {
    const {unAuthed} = this.props
    const {emailPreferences, signature, wellnessLiveNotifications} = this.state

    const uuid = unAuthed ? this.props.match.params.userId : authentication.user().uuid

    const unsubscribedEmail = Object.keys(emailPreferences).filter((p) => !emailPreferences[p as keyof CommunicationPreferences])

    const submit = marketingService
      .updateProfile({
        uuid,
        signature,
        settings: {
          emailSubscribed: isCommunicationMethodEnabled(emailPreferences)
            ? MarketingSubscriptionState.OptedIn
            : MarketingSubscriptionState.Unsubscribed,
          unsubscribedEmail,
          wellnessLiveNotifications
        }
      })
      .then(() => {
        if (unAuthed) {
          History.replace("/marketing/prefs/success")
        } else {
          this.toggleSuccessModal(true)
          setTimeout(() => this.toggleSuccessModal(false), 2000)
        }
      })

    this.setState({submit})
    return submit
  }

  render() {
    const {isMobile, unAuthed, formatMessage} = this.props
    const {emailPreferences, showSuccessModal, load, submit, isAdmin} = this.state
    const ToggleTypeset = isMobile ? Body : H6

    return (
      <Loading load={load}>
        <ConditionalWrapper condition={unAuthed} wrapper={(children) => <Grid>{children}</Grid>}>
          {unAuthed && <Banner preference={getPreferenceFromUrl()} onClickUnsubscribe={this.handleClickUnsubscribe} />}
          {!unAuthed && (
            <>
              <H2 marginBottom={1}>
                <FormattedMessage id="platform.contactPreferences.email.title" />
              </H2>
              <Body small marginBottom={5}>
                <FormattedMessage
                  id="platform.contactPreferences.email.desc"
                  values={{
                    userEmail: (
                      <>
                        <br />
                        {authentication.user().email}
                      </>
                    )
                  }}
                />
              </Body>
            </>
          )}
          <LabelWrapper isExternalPage={unAuthed}>
            <EmailLabel
              direction="column"
              alignItems="center"
              onKeyDown={(e) => onEnter(e, () => this.updateAllPreferences())}
              aria-labelledby={formatMessage({id: "platform.contactPreferences.icons.email"})}>
              <Icon icon={IconTypes.email} />
              <ToggleTypeset color="ink" marginTop={1}>
                <FormattedMessage id="platform.contactPreferences.icons.email" />
              </ToggleTypeset>
            </EmailLabel>
          </LabelWrapper>
          <Flex justifyContent="space-between" alignItems="flex-end">
            <FlexH2 marginBottom={1} isExternalPage={unAuthed}>
              <FormattedMessage id="platform.contactPreferences.communications" />
            </FlexH2>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <FlexBody small>
              <FormattedMessage id="platform.contactPreferences.allowAll.body" />
            </FlexBody>
            <ToggleWrapper isExternalPage={unAuthed}>
              <div id="toggleEmailId">
                <Toggle
                  icons={false}
                  checked={isCommunicationMethodEnabled(emailPreferences)}
                  onClick={() => this.updateAllPreferences()}
                  tabIndex={-1}
                />
              </div>
            </ToggleWrapper>
          </Flex>
          <H3 margin={[5, 0]}>
            <FormattedMessage id="platform.contactPreferences.perkboxUpdates.header" />
          </H3>
          <ContactPreference
            title="platform.contactPreferences.freePerks.title"
            description="platform.contactPreferences.freePerks.desc"
            checkboxName="freePerks"
            email={emailPreferences.free_perks}
            onToggleEmail={() => this.updatePreference("free_perks", !emailPreferences.free_perks)}
            renderHR
            isExternalPage={unAuthed}
          />
          <ContactPreference
            title="platform.contactPreferences.weeklyNewsletter.title"
            description="platform.contactPreferences.weeklyNewsletter.desc"
            checkboxName="weeklyNewsletter"
            email={emailPreferences.weekly_newsletter}
            onToggleEmail={() => this.updatePreference("weekly_newsletter", !emailPreferences.weekly_newsletter)}
            renderHR
            isExternalPage={unAuthed}
          />
          <ContactPreference
            title="platform.contactPreferences.recognitionAlerts.title"
            description="platform.contactPreferences.recognitionAlerts.desc"
            checkboxName="recognitionAlerts"
            email={emailPreferences.recognition_alerts}
            onToggleEmail={() => this.updatePreference("recognition_alerts", !emailPreferences.recognition_alerts)}
            renderHR
            isExternalPage={unAuthed}
          />
          <ContactPreference
            title="platform.contactPreferences.wellnessAlerts.title"
            description="platform.contactPreferences.wellnessAlerts.desc"
            checkboxName="wellnessAlerts"
            email={emailPreferences.wellness_alerts}
            onToggleEmail={() => this.updatePreference("wellness_alerts", !emailPreferences.wellness_alerts)}
            isExternalPage={unAuthed}
          />
          {isAdmin && (
            <>
              <H3 margin={[5, 0]}>
                <FormattedMessage id="platform.contactPreferences.adminUpdates.header" />
              </H3>
              <ContactPreference
                title="platform.contactPreferences.HRTips.title"
                description="platform.contactPreferences.HRTips.desc"
                checkboxName="hrTips"
                email={emailPreferences.hr_tips}
                onToggleEmail={() => this.updatePreference("hr_tips", !emailPreferences.hr_tips)}
                isExternalPage={unAuthed}
                renderHR
              />
              <ContactPreference
                title="platform.contactPreferences.productContent.title"
                description="platform.contactPreferences.productContent.desc"
                checkboxName="productContent"
                email={emailPreferences.product_content}
                onToggleEmail={() => this.updatePreference("product_content", !emailPreferences.product_content)}
                isExternalPage={unAuthed}
              />
            </>
          )}
          <H3 margin={[5, 0]}>
            <FormattedMessage id="platform.contactPreferences.sensitiveTopics.header" />
          </H3>
          <ContactPreference
            title="platform.contactPreferences.alcohol.title"
            description="platform.contactPreferences.alcohol.desc"
            checkboxName="alcohol"
            email={emailPreferences.alcohol}
            onToggleEmail={() => this.updatePreference("alcohol", !emailPreferences.alcohol)}
            isExternalPage={unAuthed}
            renderHR
          />
          <ContactPreference
            title="platform.contactPreferences.fathersDay.title"
            description="platform.contactPreferences.fathersDay.desc"
            checkboxName="fathersDay"
            email={emailPreferences.fathers_day}
            onToggleEmail={() => this.updatePreference("fathers_day", !emailPreferences.fathers_day)}
            isExternalPage={unAuthed}
            renderHR
          />
          <ContactPreference
            title="platform.contactPreferences.mothersDay.title"
            description="platform.contactPreferences.mothersDay.desc"
            checkboxName="mothersDay"
            email={emailPreferences.mothers_day}
            onToggleEmail={() => this.updatePreference("mothers_day", !emailPreferences.mothers_day)}
            isExternalPage={unAuthed}
          />
          <Flex direction="column" justifyContent="flex-end" marginTop={3}>
            <Loading load={submit} useLoader={false} />
            <SubmitButton load={submit} primary onClick={this.handleUpdate} marginTop={4}>
              <FormattedMessage id="platform.contactPreferences.buttons.update" />
            </SubmitButton>
          </Flex>
        </ConditionalWrapper>
        <GenericModal image={successImage} hideActions show={showSuccessModal} onHide={() => this.toggleSuccessModal(false)}>
          <H2 marginBottom={1}>
            <FormattedMessage id="platform.contactPreferences.success.modal.header" />
          </H2>
          <Body>
            <FormattedMessage id="platform.contactPreferences.success.modal.body" />
          </Body>
        </GenericModal>
      </Loading>
    )
  }
}

const mapStateToProps = ({mq}: ReduxState) => ({isMobile: mq.lessThan.md})

export default injectIntl(connect(mapStateToProps)(ContactPreferences))
